/**
 *
 * "Generate a radial menu of circles for maintenance selectors,
 * This component showed after start maintenance"
 *
 * @file   MaintenanceSelector.js
 * @author Lateral
 * @since  2023
 */

import { Button, Zoom, Tooltip } from '@mui/material'
import { useCurrentUser, useMaintenance } from 'hooks'
import React, { useCallback, useMemo, useState } from 'react'
import { SCOPES } from 'components'
import { useNavigate } from 'react-router-dom'
import { useNetworkState } from 'react-use'
import { useWearAppAnalysisContext } from './wearapp/WearAppAnalysisContext'
import { isPanelTheSame } from 'pages/common/maintenance/logic'
import dayjs from 'dayjs'
import { WearInputModal } from './WearInputModal'
import uniqBy from 'lodash.uniqby'
import { DeckMappToolTip } from 'components/tooltip/DeckMappToolTip'
import { useQuery } from '@tanstack/react-query'
import { isWearAppAvailable } from 'service/panelCornerDetection'

const MaintenanceSelectorButton = ({ onClick, selected, buttonStyling, isDisabled, children }) => {
  const show = selected.length > 0

  return (
    <Zoom in={show}>
      <Button className="rosette" variant="contained" onClick={onClick} disabled={isDisabled} sx={buttonStyling}>
        {children}
      </Button>
    </Zoom>
  )
}

const MaintenanceSelector = React.memo(function MaintenanceSelector_({
  isMobile,
  mouseCanvasPosition,
  selected,
  deckRevision,
  maintenance,
  onMaintenanceTypeClick,
  onWearSubmit,
  isPanelQueryFetching,
  isPanelQuerySuccess,
  isPanelDataQueryValid,
  setMaintenanceData,
  database
}) {
  /**
   * Generates radial menu for maintenance selector and wearApp
   * @function
   *
   * @param {boolean} isMobile - If the device is mobile then true elce false
   * @param {object} mouseCanvasPosition - Postion of the mouse while clicked on the deck
   * @param {object[]} selected - Array of selected panel objects
   * @param {string} selected.MaintenanceState - State of the maintenance such as ""NoActionRequired""
   * @param {string} selected.MaterialNumber - Serial number of the material of the deck
   * @param {object} selected.postion - Row and column postion of the deck
   * @param {object} deckRevision - Object of deckrevion details
   * @param {object} maintenance - Last record of deck maintenance
   * @param {} onMaintenanceTypeClick - Action on clicking maintenance type menu item
   * @param {} onWearSubmit - Action on clicking wear item
   * @param {} onWearAppSubmit - Action on clicking wearApp menu
   *
   * @returns Radial menu element with Wear. maintenance menu items, and wearApp button
   */
  const buttonSize = 6
  const middleX = mouseCanvasPosition.x / 16 + (isMobile ? 0 : 8)
  const middleY = mouseCanvasPosition.y / 16 + (isMobile ? 0 : 16)
  const radius = buttonSize + 3
  const angle45 = Math.PI / 4
  const cosCalculation = radius * Math.cos(angle45)
  const sinCalculation = radius * Math.sin(angle45)
  const [isWearModalOpen, setIsWearModalOpen] = useState(false)
  const { actions } = useMaintenance()
  const show = selected.length > 0
  const isDisabled = selected.length > 1
  const navigate = useNavigate()
  const { online } = useNetworkState()
  const { currentUser } = useCurrentUser()
  const hasWearApp = currentUser?.hasAllScopes([SCOPES.wearapp.Read], deckRevision.SiteId)
  const { onViewResultsPressed } = useWearAppAnalysisContext()
  const wearAppResults = useMemo(() => {
    if (!selected?.[0]) return null

    return (maintenance?.Details ?? []).find(
      ({ HistoryAction, Panel }) => HistoryAction === actions.ViewWearAppResult.id && isPanelTheSame(Panel, selected[0])
    )
  }, [maintenance?.Details, selected, actions.ViewWearAppResult.id])

  const wearAppAvailabilityRequest = useQuery(['wearAppAvailabilityCheck'], {
    queryFn: isWearAppAvailable,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })

  const onWearModalClosed = useCallback(() => {
    setIsWearModalOpen(false)
  }, [])

  const hasWearAppResults = Boolean(wearAppResults)
  const isWearAppDisabled =
    wearAppAvailabilityRequest.isFetching ||
    !wearAppAvailabilityRequest.data ||
    (!hasWearAppResults &&
      (!hasWearApp || !online || isPanelQueryFetching || (isPanelQuerySuccess && !isPanelDataQueryValid)))

  const buttonStyling = isMobile
    ? {
        textTransform: 'capitalize',
        position: isMobile ? 'absolute' : undefined,
        height: `${buttonSize}em`,
        width: `${buttonSize}em`,
        borderRadius: '50%',
        backgroundColor: `#FFAE57`,
        '&:hover': {
          backgroundColor: `#FFAE57`
        }
      }
    : {
        textTransform: 'capitalize',
        width: '100%',
        backgroundColor: `#FFAE57`,
        '&:hover': {
          backgroundColor: `#FFAE57`
        }
      }

  const getDisableReason = useCallback(() => {
    if (!wearAppAvailabilityRequest.data) {
      return 'WearApp is disabled'
    }
    if (!hasWearApp) {
      return "You don't have access to the WearApp package"
    }
    if (!online) {
      return 'You are not connected to the Internet'
    }
    if (isPanelQueryFetching) {
      return 'WearApp is loading'
    }
    if (isPanelQuerySuccess && !isPanelDataQueryValid) {
      return `Wear analysis cannot be performed on the selected panel(${selected[0]?.MaterialNumber})`
    }
    return ''
  }, [
    wearAppAvailabilityRequest.data,
    hasWearApp,
    online,
    isPanelQueryFetching,
    isPanelQuerySuccess,
    isPanelDataQueryValid,
    selected
  ])
  const wearAppButtonStyling = useMemo(() => {
    if (!isWearAppDisabled) {
      return buttonStyling
    }

    // eslint-disable-next-line no-unused-vars
    const { '&:hover': _, ...restStyles } = buttonStyling
    return {
      ...restStyles,
      cursor: 'not-allowed'
    }
  }, [isWearAppDisabled, buttonStyling])

  const hasAction = useMemo(
    () =>
      (maintenance.Details ?? []).some(({ HistoryAction, Panel, WearAppResponse }) => {
        if (!selected?.[0] || !isPanelTheSame(Panel, selected[0]) || HistoryAction === actions.ViewWearAppResult.id) {
          return false
        }
        return WearAppResponse?.length !== 0 && HistoryAction !== actions.NoChange.id
      }),
    [maintenance.Details, selected, actions.ViewWearAppResult.id, actions.NoChange.id]
  )

  const onClearPress = useCallback(() => {
    if (!hasAction) {
      return
    }

    const clone = structuredClone(maintenance)
    const wearAppReplaceActions = []

    for (const selectedPanel of selected) {
      const wearAppReplaceAction = clone.Details.find(
        ({ Panel, WearAppResponse, HistoryAction }) =>
          isPanelTheSame(Panel, selectedPanel) &&
          WearAppResponse?.length > 0 &&
          (HistoryAction === actions.Replace.id || WearAppResponse === 'add-wear-action')
      )

      if (wearAppReplaceAction) {
        wearAppReplaceActions.push({ ...structuredClone(wearAppReplaceAction), HistoryAction: actions.NoChange.id })
      }
    }

    clone.Details = clone.Details.filter(
      ({ Panel, SwappedPanel }) =>
        !selected.some(
          (selectedPanel) => isPanelTheSame(Panel, selectedPanel) || isPanelTheSame(SwappedPanel, selectedPanel)
        )
    )

    clone.Details.push(...wearAppReplaceActions)

    setMaintenanceData((current) => {
      const maintenanceDataClone = structuredClone(current)
      maintenanceDataClone.deckRevisionHistory = clone
      return maintenanceDataClone
    })
  }, [hasAction, maintenance, selected, actions.Replace.id, actions.NoChange.id, setMaintenanceData])
  const selectedPanels = useMemo(() => {
    return uniqBy(selected, (item) => item.Position)
  }, [selected])

  const WearAppComponent = (
    <Zoom in={show}>
      <Button
        className="rosette"
        variant="contained"
        component="label"
        disabled={isWearAppDisabled}
        style={isWearAppDisabled ? { pointerEvents: 'all', cursor: 'default' } : {}}
        onClick={(e) => {
          e.stopPropagation()
          if (hasWearAppResults) {
            onViewResultsPressed(wearAppResults.extraData.uuid)
            return
          }
          if (isWearAppDisabled) {
            return
          }
          navigate('wearapp')
        }}
        sx={[
          wearAppButtonStyling,
          isMobile
            ? {
                top: `${middleY + radius}em`,
                left: `${middleX}em`
              }
            : undefined
        ]}>
        {hasWearAppResults ? 'WearApp Results' : 'WearApp'}
      </Button>
    </Zoom>
  )

  const WearAppComponentContainer = isWearAppDisabled ? (
    <Tooltip title={getDisableReason()} followCursor>
      <div>{WearAppComponent}</div>
    </Tooltip>
  ) : (
    WearAppComponent
  )

  return (
    <>
      <DeckMappToolTip
        title={
          <>
            Wear = Enter manual panel depth of wear measurement <br />
            Rotate = Rotate panel 180° <br />
            Swap = Swap two panels <br />
            Relocate = Relocate panel and replace original panel <br />
            Replace = Replace panel <br />
            WearApp = Open WearApp application to measure wear and estimate panel service life <br />
            Patched = wear resistant compound applied to the item <br />
            Clear = Clear previously selected panel action
          </>
        }
      />
      {/* The actual radial menu */}
      <MaintenanceSelectorButton
        selected={selected}
        onClick={(e) => {
          e.stopPropagation()
          setIsWearModalOpen(true)
        }}
        buttonStyling={[
          buttonStyling,
          isMobile
            ? {
                top: `${middleY.minus(cosCalculation)}em`,
                left: `${middleX.minus(sinCalculation)}em`
              }
            : undefined
        ]}>
        Wear
      </MaintenanceSelectorButton>

      <MaintenanceSelectorButton
        selected={selected}
        buttonStyling={[
          buttonStyling,
          isMobile ? { top: `${middleY.minus(radius)}em`, left: `${middleX}em` } : undefined
        ]}
        onClick={(e) => {
          e.stopPropagation()
          onMaintenanceTypeClick(actions.Rotate)
        }}>
        Rotate
      </MaintenanceSelectorButton>

      <MaintenanceSelectorButton
        selected={selected}
        buttonStyling={[
          buttonStyling,
          isMobile
            ? {
                top: `${middleY.minus(cosCalculation)}em`,
                left: `${middleX.plus(sinCalculation)}em`
              }
            : undefined
        ]}
        onClick={(e) => {
          e.stopPropagation()
          onMaintenanceTypeClick(actions.Swap)
        }}
        isDisabled={isDisabled}>
        Swap
      </MaintenanceSelectorButton>

      <MaintenanceSelectorButton
        selected={selected}
        onClick={(e) => {
          e.stopPropagation()
          onMaintenanceTypeClick(actions.Relocate)
        }}
        isDisabled={isDisabled}
        buttonStyling={[
          buttonStyling,
          isMobile
            ? {
                top: `${middleY}em`,
                left: `${middleX.plus(radius)}em`
              }
            : undefined
        ]}>
        Relocate
      </MaintenanceSelectorButton>

      <MaintenanceSelectorButton
        selected={selected}
        onClick={(e) => {
          e.stopPropagation()
          onMaintenanceTypeClick(actions.Replace, { PanelReplaceDate: dayjs().toISOString() })
        }}
        buttonStyling={[
          buttonStyling,
          isMobile
            ? {
                top: `${middleY + cosCalculation}em`,
                left: `${middleX + sinCalculation - radius * 1.4}em`
              }
            : undefined
        ]}>
        Replace
      </MaintenanceSelectorButton>

      {WearAppComponentContainer}
      {hasAction && (
        <MaintenanceSelectorButton
          selected={selected}
          onClick={(e) => {
            e.stopPropagation()
            onClearPress()
          }}
          buttonStyling={[
            buttonStyling,
            isMobile
              ? {
                  top: `${middleY.plus(cosCalculation)}em`,
                  left: `${middleX.plus(sinCalculation).minus(radius * 1.4)}em`
                }
              : undefined
          ]}>
          Clear
        </MaintenanceSelectorButton>
      )}

      <MaintenanceSelectorButton
        selected={selected}
        onClick={(e) => {
          e.stopPropagation()
          onMaintenanceTypeClick(actions.Patched)
        }}
        buttonStyling={[
          buttonStyling,
          isMobile
            ? {
                top: `${middleY.plus(radius).minus(2)}em`,
                left: `${middleX.minus(radius).times(1.5)}em`
              }
            : undefined
        ]}>
        Patch
      </MaintenanceSelectorButton>
      {isWearModalOpen && (
        <WearInputModal
          deckRevision={deckRevision}
          maintenance={maintenance}
          handleClose={onWearModalClosed}
          isMobile={isMobile}
          onWearSubmit={onWearSubmit}
          selectedPanels={selectedPanels}
          database={database}
        />
      )}
    </>
  )
})

export default MaintenanceSelector
